import type { SupabaseClientLike } from './people';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface RoleUIElement {
  element_key: string;
  visibility: 'visible' | 'hidden';
  mode: 'edit' | 'view';
}

export interface RoleUIElementInput {
  element_key: string;
  visibility: 'visible' | 'hidden';
  mode: 'edit' | 'view';
}

export interface RoleRecord {
  id: string;
  name: string;
  label: string;
  description?: string | null;
  is_system?: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

async function getAuthHeaders(supabase: SupabaseClientLike): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  return token ? { authorization: `Bearer ${token}` } : {};
}

async function appFetch<T>(
  supabase: SupabaseClientLike,
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const headers = await getAuthHeaders(supabase);
  const supabaseUrl = (supabase as unknown as { supabaseUrl: string }).supabaseUrl ?? '';
  const base = supabaseUrl.replace('/rest/v1', '');
  const res = await fetch(`${base}/functions/v1/app${path}`, {
    ...init,
    headers: { 'content-type': 'application/json', ...headers, ...(init.headers ?? {}) },
  });
  if (!res.ok) {
    const body = await res.text();
    throw new Error(body || `HTTP ${res.status}`);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

// ---------------------------------------------------------------------------
// API functions
// ---------------------------------------------------------------------------

async function list(supabase: SupabaseClientLike, tenantId: string): Promise<RoleRecord[]> {
  const { data, error } = await (supabase as any)
    .from('roles')
    .select('id, name, label, description, is_system')
    .eq('tenant_id', tenantId)
    .order('name');
  if (error) throw error;
  return (data ?? []) as RoleRecord[];
}

async function getUIElementsForRole(
  supabase: SupabaseClientLike,
  tenantId: string,
  roleId: string,
): Promise<RoleUIElement[]> {
  const { data, error } = await (supabase as any)
    .from('role_ui_elements')
    .select('element_key, visibility, mode')
    .eq('tenant_id', tenantId)
    .eq('role_id', roleId);
  if (error) throw error;
  return (data ?? []) as RoleUIElement[];
}

async function setUIElement(
  supabase: SupabaseClientLike,
  tenantId: string,
  roleId: string,
  key: string,
  visibility: 'visible' | 'hidden',
  mode: 'edit' | 'view',
): Promise<void> {
  const { error } = await (supabase as any)
    .from('role_ui_elements')
    .upsert(
      { tenant_id: tenantId, role_id: roleId, element_key: key, visibility, mode },
      { onConflict: 'tenant_id,role_id,element_key' },
    );
  if (error) throw error;
}

async function setUIElements(
  supabase: SupabaseClientLike,
  tenantId: string,
  roleId: string,
  rules: RoleUIElementInput[],
): Promise<void> {
  const rows = rules.map((r) => ({
    tenant_id: tenantId,
    role_id: roleId,
    element_key: r.element_key,
    visibility: r.visibility,
    mode: r.mode,
  }));
  const { error } = await (supabase as any)
    .from('role_ui_elements')
    .upsert(rows, { onConflict: 'tenant_id,role_id,element_key' });
  if (error) throw error;
}

async function cloneRole(
  supabase: SupabaseClientLike,
  tenantId: string,
  sourceRoleId: string,
  newName: string,
  newLabel: string,
): Promise<RoleRecord> {
  const result = await appFetch<{ role: RoleRecord }>(supabase, '/roles/clone', {
    method: 'POST',
    body: JSON.stringify({ sourceRoleId, newName, newLabel, cloneUIElements: true }),
    headers: { 'x-tenant-id': tenantId },
  });
  return result.role;
}

async function resetRoleUIElements(
  supabase: SupabaseClientLike,
  tenantId: string,
  roleId: string,
): Promise<void> {
  const { error } = await (supabase as any)
    .from('role_ui_elements')
    .delete()
    .eq('tenant_id', tenantId)
    .eq('role_id', roleId);
  if (error) throw error;
}

export const roles = {
  list,
  getUIElementsForRole,
  setUIElement,
  setUIElements,
  cloneRole,
  resetRoleUIElements,
};
